import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiInstance } from '../services/service-trips-api/service-trips-api';
import { toScheduleId } from '../services/service-trips-api/types';
import type { SchedulePendingReviewResponse } from '../services/service-trips-api/types';
import { QueryKeys } from './types';

export const useSchedulePendingReviewQuery = (
  scheduleId: number | undefined,
  enabled = false
): UseQueryResult<SchedulePendingReviewResponse> =>
  useQuery<SchedulePendingReviewResponse>({
    queryKey: [QueryKeys.SchedulePendingReview, scheduleId],
    queryFn: () => {
      if (scheduleId === undefined) {
        throw new Error('scheduleId is required when query is enabled');
      }
      return apiInstance.getSchedulePendingReview(toScheduleId(scheduleId));
    },
    enabled: enabled && !!scheduleId,
  });

/**
 * Fetches pending-review data for a dynamic list of schedule ids (e.g. all schedules
 * currently in the "Edited" state for a passenger). Uses the same query key as
 * `useSchedulePendingReviewQuery` so results are shared/deduped across both hooks.
 */
export const useSchedulesPendingReviewQueries = (
  scheduleIds: number[],
  enabled = false
): UseQueryResult<SchedulePendingReviewResponse>[] =>
  useQueries({
    queries: scheduleIds.map((scheduleId) => ({
      queryKey: [QueryKeys.SchedulePendingReview, scheduleId],
      queryFn: () => apiInstance.getSchedulePendingReview(toScheduleId(scheduleId)),
      enabled: enabled && !!scheduleId,
    })),
  });

export const useScheduleApproveMutation = (): UseMutationResult<void, Error, number> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId: number) =>
      apiInstance.scheduleAction(toScheduleId(scheduleId), 'approve'),
    onSuccess: (_data, scheduleId) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.PassengerDetails] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.SchedulePendingReview, scheduleId] });
    },
  });
};

export const useScheduleRejectMutation = (): UseMutationResult<void, Error, number> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId: number) =>
      apiInstance.scheduleAction(toScheduleId(scheduleId), 'reject'),
    onSuccess: (_data, scheduleId) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.PassengerDetails] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.SchedulePendingReview, scheduleId] });
    },
  });
};
